import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';
import { ReactNode, useEffect, useRef, useState } from 'react';

import { ImageTool, mediaTemplates } from '@/constants/mediaTemplates';
import { scrollToSection, WORKSHOP_CONTACT_ID } from '@/utils/scrollToSection';

function LazyOnView({
  placeholder,
  children,
}: {
  placeholder: ReactNode;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || visible) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [visible]);

  return (
    <div ref={ref} className='h-full w-full'>
      {visible ? children : placeholder}
    </div>
  );
}

// The 8 most workshop-relevant outputs, sourced from the shared media
// templates so titles, descriptions, links, and media stay in sync.
const WORKSHOP_SHOWCASE_SLUGS = [
  'generate-website', // Marky agent: build full websites
  'ai-slide-decks', // Marky agent: presentations
  'product-showcase-reference', // Product video (Seedance 2.0)
  'social-media-video-ad', // Marketing video ad (Veo 3.1)
  'ugc-product-review', // UGC marketing video
  'workflow-product-commercial', // End-to-end commercial workflow
  'realtor-property-tour', // Business use case
  'professional-headshot', // Named in the workshop curriculum
];

const showcaseItems: ImageTool[] = WORKSHOP_SHOWCASE_SLUGS.map((slug) =>
  mediaTemplates.find((template) => template.slug === slug),
).filter((item): item is ImageTool => Boolean(item));

const isVideoSrc = (item: ImageTool) =>
  item.isVideoPreview || /\.(mp4|webm)(\?|$)/i.test(item.image);

const WorkshopFeatures = () => {
  const { t } = useLocale('ai-workshops');

  return (
    <section
      id='features'
      className='border-b border-slate-100 bg-slate-50 py-24 dark:border-slate-900 dark:bg-slate-950 sm:py-32'
    >
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <div className='mx-auto max-w-3xl text-center'>
          <h2 className='text-base font-semibold leading-7 text-indigo-600 dark:text-indigo-500'>
            {t("What You'll Create")}
          </h2>
          <p className='mt-2 text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100 sm:text-4xl'>
            {t('Achieve more by mastering AI tools')}
          </p>
          <p className='mt-4 text-lg leading-8 text-slate-600 dark:text-slate-400'>
            {t(
              'A glimpse of what you will build hands-on: websites, decks, product videos, and marketing content, all generated in minutes.',
            )}
          </p>
        </div>

        <div className='mx-auto mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4'>
          {showcaseItems.map((item) => {
            const isVideo = isVideoSrc(item);

            return (
              <Link
                key={item.slug}
                href={item.uri}
                className='group flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-md dark:border-slate-800 dark:bg-slate-800/60'
              >
                <div className='relative aspect-[4/3] overflow-hidden bg-slate-100 dark:bg-slate-900'>
                  <span className='absolute left-3 top-3 z-10 inline-flex items-center rounded-full bg-slate-900/80 px-2.5 py-0.5 text-xs font-semibold text-white'>
                    {isVideo
                      ? t('AI Video')
                      : item.title === 'AI Slide Decks' ||
                          item.title === 'Build Websites'
                        ? t('Marky Agent')
                        : item.title === 'Product Commercial'
                          ? t('AI Workflows')
                          : t('AI Image')}
                  </span>
                  {isVideo ? (
                    <LazyOnView
                      placeholder={
                        <div className='h-full w-full animate-pulse bg-slate-100 dark:bg-slate-900' />
                      }
                    >
                      <video
                        className='h-full w-full object-cover transition-transform duration-500 group-hover:scale-105'
                        autoPlay
                        loop
                        muted
                        playsInline
                        preload='none'
                      >
                        <source
                          src={item.image}
                          type={
                            /\.webm(\?|$)/i.test(item.image)
                              ? 'video/webm'
                              : 'video/mp4'
                          }
                        />
                      </video>
                    </LazyOnView>
                  ) : (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={item.image}
                      alt={item.title}
                      loading='lazy'
                      className='h-full w-full object-cover transition-transform duration-500 group-hover:scale-105'
                    />
                  )}
                </div>

                <div className='flex flex-1 flex-col p-5'>
                  <h3 className='text-base font-bold text-slate-900 dark:text-slate-100'>
                    {item.title}
                  </h3>
                  <p className='mt-2 line-clamp-3 text-sm leading-relaxed text-slate-600 dark:text-slate-400'>
                    {item.description}
                  </p>
                  <span className='mt-4 inline-flex items-center gap-1 text-sm font-semibold text-indigo-600 dark:text-indigo-500'>
                    {t('Try it')}
                    <svg
                      className='h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5'
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'
                      strokeWidth='2'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        d='M17 8l4 4m0 0l-4 4m4-4H3'
                      />
                    </svg>
                  </span>
                </div>
              </Link>
            );
          })}
        </div>

        <div className='mt-16 flex justify-center'>
          <a
            href={`#${WORKSHOP_CONTACT_ID}`}
            onClick={scrollToSection}
            className='inline-flex items-center justify-center rounded-xl bg-indigo-600 px-8 py-4 text-base font-bold text-white shadow-sm transition-all duration-150 hover:bg-indigo-500 hover:shadow'
          >
            {t('I Want To Make AI Work For Me')}
          </a>
        </div>
      </div>
    </section>
  );
};

export default WorkshopFeatures;
